import type { ReportTemplate } from './reportTemplate.js';
import type { PortalGoogleAutomotiveReport, PortalMetaAutomotiveReport } from './portalAutomotive.js';
import type { AdChannel } from './advertisingHome.js';
import type {
  CurrencyAmount,
  ReportingDemographicsDimension,
  ReportingDemographicsPeriod,
} from './reporting.js';

// CLIENT PORTAL Phase 1 — the client report's shared shapes.
//
// Imported by BOTH the server assembly (server/modules/clientPortal) and the
// pages (src/modules/clientPortal/portal/report), per the shared-types rule.
//
// ⚠️⚠️ THE LEAD DEFINITIONS ARE THE INTERNAL ONES, DELIBERATELY. Meta = the
// derived `total_leads` (leadDerivation); Google = the STANDARD `conversions`
// metric, never `all_conversions`. A client's number disagreeing with what their
// account manager sees internally is a credibility problem, not a rounding one —
// so the portal reads the SAME spine functions the AM dashboard does rather than
// restating the rule in a second query.
//
// ⚠️ MONEY IS NATIVE CURRENCY ONLY. The agency display-currency conversion is an
// internal cross-portfolio tool; a client sees their own currency, unconverted,
// with no selector. A scope is single-currency in practice, but the wire shape
// carries the currency per figure so a mixed scope renders per-currency rather
// than silently blending.

export type { AdChannel };

/** Which figure a chart plots. CPL is always re-derived, never averaged. */
export type PortalMetric = 'leads' | 'costPerLead' | 'spend';

export const PORTAL_METRICS: readonly PortalMetric[] = ['leads', 'costPerLead', 'spend'];

/** The period chart's grouping switch. The 13-month chart is always monthly. */
export type PortalGrouping = 'daily' | 'monthly';

/**
 * What we know about a bucket beyond its numbers.
 *
 * ⚠️⚠️ `no-data` IS THE WHOLE POINT OF THIS TYPE. Reporting backfill is tiered —
 * some accounts hold ~25 months, others only the recent 90 days — so a month
 * before an account's coverage begins has NO measurement, which is a completely
 * different statement from "you spent nothing". An empty bar tells a client they
 * got nothing when the truth is that we were not measuring yet. A `no-data`
 * bucket renders as an explicit GAP with an explanation, and its `leads` /
 * `spend` are NULL rather than 0 so nothing downstream can sum it into a total.
 */
export type PortalBucketCoverage =
  /** Measured for the whole bucket. */
  | 'complete'
  /** Measured, but the bucket is not finished yet (the current month/day). */
  | 'in-progress'
  /**
   * Measured for only PART of the bucket — the month reporting began, or (on a
   * multi-account scope) a month where some accounts were covered and others
   * were not. The figure is real but understates; it is plotted and flagged.
   */
  | 'partial'
  /** Not measured at all. ⚠️ Renders as a gap, NEVER as zero. */
  | 'no-data';

/**
 * One point on a chart. ⚠️ Every figure is `number | null`, and null means
 * UNMEASURED — never zero. A `no-data` bucket has all three null; a measured
 * bucket with genuine zero spend has `spend: 0`, which is a real fact.
 */
export interface PortalSeriesPoint {
  /** YYYY-MM-DD for daily buckets, YYYY-MM for monthly. */
  bucket: string;
  /** Human label ("14 Aug", "Aug 2026"). Server-formatted so both charts agree. */
  label: string;
  coverage: PortalBucketCoverage;
  leads: number | null;
  spend: number | null;
  /**
   * ⚠️ RE-DERIVED PER BUCKET (this bucket's spend ÷ this bucket's leads), never
   * an average of daily CPLs. NULL when the bucket has spend but NO leads — a
   * cost per lead of nothing is not zero, it is undefined, and plotting 0 there
   * would read as "leads became free".
   */
  costPerLead: number | null;
  /**
   * Delivery counts — what the ads were SHOWN and CLICKED, as opposed to what
   * they returned.
   *
   * ⚠️⚠️ TWO COUNTS, AND DELIBERATELY NOT FIVE FIELDS. Click-through rate, cost
   * per 1,000 and cost per click all derive from these two plus the `spend`
   * already here, per bucket, by the same rule `costPerLead` follows. Carrying
   * the three ratios on the wire would create three more chances for a bucket's
   * ratio to disagree with its own numerator.
   *
   * ⚠️ NULL means UNMEASURED, exactly like `leads` and `spend` — a `no-data`
   * bucket has all of them null, and the sparklines break rather than drawing a
   * zero. A measured month with genuinely no impressions is `0`, which is a
   * real fact.
   */
  impressions: number | null;
  clicks: number | null;
}

/** Period totals, in one native currency. */
export interface PortalTotals {
  currency: string;
  leads: number;
  spend: number;
  /** Null when there were no leads — see PortalSeriesPoint.costPerLead. */
  costPerLead: number | null;
}

/**
 * What is known about how far back the data goes, for this scope and channel.
 * Drives the `no-data` / `partial` bucket states and the sentence under the
 * 13-month chart.
 */
export interface PortalCoverage {
  /** YYYY-MM-DD of the earliest measured day for ANY account in scope. */
  firstDate: string | null;
  /**
   * YYYY-MM-DD from which EVERY account in scope is measured (the latest of the
   * per-account first dates). Between `firstDate` and this, an aggregate figure
   * is real but incomplete — so those months are flagged `partial`.
   *
   * ⚠️ Accounts with NO rows at all on this channel are EXCLUDED from the
   * calculation: a location that has never run Google ads has no Google coverage
   * question, and letting it push this date forward would gap months that are
   * genuinely complete for everyone who was running.
   */
  fullFromDate: string | null;
  /** How many accounts in scope have any data at all on this channel. */
  accountsWithData: number;
  accountsInScope: number;
}

/**
 * A headline figure's movement against the chosen comparison period.
 *
 * ⚠️ `null` where the comparison period is UNMEASURED — never a fabricated
 * −100%. A client whose backfill starts inside the comparison window would
 * otherwise be told their leads fell to nothing, which is the report's own
 * no-data-is-not-zero rule showing up on a different surface.
 */
export interface PortalDelta {
  /** Signed percentage change, one decimal. */
  pct: number;
  direction: 'up' | 'down' | 'flat';
}

/**
 * What the server sends so the client can compute deltas.
 *
 * ⚠️ The server resolves the WINDOW (it decides what to fetch) and reports
 * whether it is honestly comparable; the client turns the two totals into a
 * percentage with the staff dashboards' own `computeDelta`. Neither half is
 * re-derived — see src/lib/portalReport/comparison.ts for why they are split.
 */
export interface PortalComparison {
  mode: PortalComparisonMode;
  range: { from: string; to: string };
  /** Per-currency, exactly like the current totals. */
  totals: PortalTotals[];
  /**
   * ⚠️ Non-null when there is no honest comparison to draw — an unmeasured or
   * partly-measured window. The UI shows this INSTEAD of a percentage, never a
   * fabricated −100%.
   */
  unavailableReason: string | null;
}

export interface PortalDeltas {
  leads: PortalDelta | null;
  spend: PortalDelta | null;
  costPerLead: PortalDelta | null;
  /** Why there is no delta, when there is none. Shown instead of a figure. */
  unavailableReason: string | null;
}

/**
 * ⚠️ REUSES the AM Advertising Home's own comparison vocabulary. A client and
 * their account manager disagreeing about what "vs last month" means is exactly
 * the credibility problem this report cannot afford.
 */
export type PortalComparisonMode = 'previous' | 'lastMonth' | 'lastYear';

/** One channel's block — the identical shape used three times on the Overview. */
export interface PortalChannelBlock {
  channel: AdChannel | 'combined';
  /** ⚠️ Per-currency; a single-currency scope has exactly one entry. */
  totals: PortalTotals[];
  /** The selected period, at the requested grouping. */
  periodSeries: PortalSeriesPoint[];
  /** 13 months up to today — the last entry is the partial current month. */
  thirteenMonthSeries: PortalSeriesPoint[];
  coverage: PortalCoverage;
  /**
   * The latest date this channel has data for, roster-wide. ⚠️ Surfaced on the
   * page: Google's transfer lands a day behind, and silence would imply live.
   */
  asOf: string | null;
  /** The comparison window and its totals. Null when none was requested. */
  comparison: PortalComparison | null;
  /**
   * RESTYLE — twelve rolling months, this year against the same months last year.
   *
   * ⚠️ Distinct from `thirteenMonthSeries`, which is ONE rolling run of months
   * and structurally cannot express a year-on-year comparison. Both are served
   * from the same daily fetch; neither is derived from the other.
   */
  yearOnYear: PortalYearOnYear;
}

/** A row of the by-location table — present only on a multi-account scope. */
export interface PortalLocationFigures {
  leads: number;
  /**
   * ⚠️ NULLABLE, and the null is a REFUSAL rather than an absence: a club
   * whose two windows are in different currencies (or which returned rows in two
   * currencies) gets no money comparison, because there is no single figure to
   * take a percentage of. Its LEADS still compare — a lead is a lead whoever
   * paid.
   */
  spend: number | null;
  costPerLead: number | null;
}

/** One club's CRM figures on the leaderboard. ⚠️ OVERVIEW ONLY — see below. */
export interface PortalLocationCrm {
  leads: number;
  digitalLeads: number;
  sales: number;
  /** ⚠️ NULL when the CRM does not cover the comparison window for this club. */
  previous: { leads: number; digitalLeads: number; sales: number } | null;
}

export interface PortalLocationRow {
  accountId: string;
  accountName: string;
  /**
   * ⚠️ NULLABLE since 2026-09-03. A club with no delivery in the period has no
   * currency of its own to report, and inheriting the first club's would label
   * one country's dashes with another's symbol.
   */
  currency: string | null;
  /**
   * ⚠️⚠️ THIS CLUB'S OWN COVERAGE OF THE SELECTED PERIOD. One combined series
   * has one coverage; twelve clubs have twelve. `no-data` means the club was not
   * being measured, and its figures are NULL — never 0, which on a leaderboard
   * reads as "this club produced nothing".
   */
  coverage: PortalBucketCoverage;
  /** ⚠️ NULL when `coverage` is `no-data`. A measured zero is 0. */
  leads: number | null;
  spend: number | null;
  costPerLead: number | null;
  /**
   * The same figures over the comparison window the CLIENT selected.
   *
   * ⚠️⚠️ NULL when the report's comparison is unavailable OR when this club's
   * own history does not fully cover that window. Both gates apply: a club whose
   * history starts inside the comparison period would otherwise carry a delta
   * against a partial count, and report a collapse.
   */
  previous: PortalLocationFigures | null;
  /**
   * ⚠️⚠️ NULL ON THE META AND GOOGLE PAGES, ALWAYS — not merely absent there
   * by accident. A sale cannot be attributed to a channel (a sale row is blank
   * in `source`, `contact_type` AND `person_id` — 188,726 of 188,726 across all
   * history), so a sales column sitting in a row of Meta's spend and leads would
   * be read as Meta's sales. The CRM columns are the OVERVIEW's alone.
   *
   * ⚠️ Also null for a club with no CRM link or no CRM history, so its columns
   * render as dashes rather than as zeros.
   */
  crm: PortalLocationCrm | null;
}

/** A campaign row, on either channel's page. */
export interface PortalCampaignRow {
  campaignId: string;
  campaignName: string;
  currency: string;
  spend: number;
  leads: number;
  costPerLead: number | null;
  impressions: number | null;
  clicks: number | null;
}

/**
 * An AD-GROUP row on the Google page's "Where the budget goes" table.
 *
 * ⚠️ It carries its PARENT CAMPAIGN's name. "Brand — Exact" means nothing on its
 * own; a client scanning ad groups across several campaigns needs to know which
 * one each sits in.
 */
export interface PortalAdGroupRow {
  adGroupId: string;
  adGroupName: string;
  campaignName: string;
  currency: string;
  spend: number;
  leads: number;
  costPerLead: number | null;
  impressions: number | null;
  clicks: number | null;
}

/**
 * ⚠️⚠️ WHAT THE AD-GROUP VIEW LEAVES OUT, so the toggle is not a trap.
 *
 * Performance Max campaigns have no ad groups at all, so switching to the
 * ad-group view REMOVES their spend from the table — it is not a rearrangement of
 * the same money. The staff dashboard surfaces this as `GoogleAdsGrainExclusion`
 * rather than letting it be discovered as a number that mysteriously dropped, and
 * a client report cannot be laxer than the staff one.
 *
 * ⚠️ `cost` is PER CURRENCY, never blended.
 */
export interface PortalAdGroupExclusion {
  campaigns: number;
  cost: CurrencyAmount[];
  /** A few campaign names, capped — the "which ones?" question. */
  names: string[];
}

export interface PortalAdGroupsResult {
  rows: PortalAdGroupRow[];
  /** Null when nothing was excluded — the sentence is then absent, not empty. */
  excluded: PortalAdGroupExclusion | null;
}

/** A Meta creative row. */
export interface PortalCreativeRow {
  adId: string;
  /**
   * The kyck account this creative's spend is attributed to.
   *
   * ⚠️⚠️ REQUIRED, AND IT IS PART OF THE CARD'S MERGE KEY (§7, 2026-09-04).
   * Cards merge on identical ad name + campaign + currency, and franchise clubs
   * run the SAME TEMPLATED ad and campaign names — so a group report was
   * silently summing spend and leads from DIFFERENT CLUBS onto one card, with
   * `mergedAdCount` reporting a row count that said nothing about clubs.
   *
   * ⚠️ NOT OPTIONAL, deliberately: it makes every construction site declare one,
   * so a row built without an account fails `tsc` rather than merging across
   * clubs at runtime. See src/lib/portalReport/creativeGrouping.ts.
   */
  accountId: string;
  adName: string | null;
  campaignName: string | null;
  currency: string;
  spend: number;
  leads: number;
  costPerLead: number | null;
  /**
   * Meta purchases and landing page views — the goal-split page's creative
   * grid shows these when filtered to Purchase or Other campaigns (round 2).
   *
   * ⚠️ OPTIONAL, and ABSENT is not zero: the Meta creatives query always sets
   * both, but a row from any other source does not carry them, and the grid then
   * falls back to the leads view rather than printing a confident 0.
   */
  purchases?: number;
  landingPageViews?: number;
  /** ⚠️ Null renders a placeholder, never a broken image. */
  thumbnailUrl: string | null;
  /**
   * Meta delivery status (`active` / `paused` / …), as stored on the fact row.
   *
   * ⚠️ Null when the pull recorded none — rendered as an ABSENT pill, never as
   * "Paused". Guessing a delivery state tells a client an ad is off when it may
   * be running.
   *
   * ⚠️ There is deliberately NO format field beside it — see the header.
   */
  status: string | null;
  /**
   * When this creative was last mirrored (`ad_creatives.refreshed_at`).
   *
   * ⚠️ PRESENTATION ONLY, and it exists for exactly one job: when several ad rows
   * merge into one card, the thumbnail is taken from the freshest of them.
   * Meta CDN URLs are short-lived, so an older row is likelier to 404 into the
   * "No preview" placeholder. See src/lib/portalReport/creativeGrouping.ts.
   *
   * ⚠️ Null is normal — a creative row with no mirror record — and sorts last
   * rather than being dropped. Nothing else reads it.
   */
  refreshedAt: string | null;
}

/** A Google search-term row. */
export interface PortalSearchTermRow {
  searchTerm: string;
  matchType: string | null;
  currency: string;
  clicks: number;
  impressions: number;
  leads: number;
  spend: number;
  /**
   * ⚠️ A SERVER-SIDE FIELD, NOT A CLIENT-SIDE DERIVATION, and that is a
   * requirement rather than a preference. `Column<T>.key` is
   * `Extract<keyof T, string>` and `sortReportRows` sorts by that key, so a value
   * computed inside `render` would be an UNSORTABLE column in a table whose own
   * intro says "Sort any column" — beside four numeric columns that all sort.
   *
   * ⚠️ Null when there were no clicks. Renders as a dash, never $0.00 — see
   * `deriveCostPerClick`.
   */
  costPerClick: number | null;
  costPerLead: number | null;
}

/** One selectable campaign in the page filter — the campaigns present in the period. */
export interface PortalCampaignOption {
  campaignId: string;
  campaignName: string;
}

/** GET /api/portal/report/overview */
/**
 * CLIENT PORTAL §8 — THE GROUP CHARTS' per-club, per-bucket series.
 *
 * ⚠️⚠️ ABSOLUTE VALUES ON THE WIRE, NEVER THE INDEX. The chart draws each series
 * indexed to 100, but the index is a DRAWING decision made at the render edge:
 * the tooltip has to carry the real figures with their units and currency, and a
 * payload that had already divided them away could not.
 *
 * ⚠️ A bucket the club was not being measured in carries NULLS, never zeros —
 * the same rule as every other series on this report, applied per club rather
 * than per scope. Twelve clubs have twelve coverages.
 */
export interface PortalGroupClubPoint {
  bucket: string;
  /** ⚠️ NULL = this club was not measured in this bucket. 0 is a real zero. */
  spend: number | null;
  /** Ad-platform conversions — the figure the leaderboard calls Ad Conversions. */
  adConversions: number | null;
  /** ⚠️ NULL for a club with no CRM link, as well as for an unmeasured bucket. */
  internetLeads: number | null;
  sales: number | null;
}

export interface PortalGroupClub {
  accountId: string;
  name: string;
  /**
   * ⚠️ NULL when this club returned MORE THAN ONE currency over the period, in
   * which case its cost series is withheld rather than blended. Money is per
   * currency here exactly as it is everywhere else on this report.
   */
  currency: string | null;
  /**
   * The club's total ad conversions over the period — the RANKING key, resolved
   * server-side so the ordering (and therefore the colour assignment) cannot
   * depend on what the reader has ticked.
   */
  volumeRank: number;
  points: PortalGroupClubPoint[];
}

export interface PortalGroupSeries {
  /** Every bucket in the window, in order, with its axis label. */
  buckets: Array<{ bucket: string; label: string }>;
  /**
   * ⚠️⚠️ THE FINAL BUCKET IS STILL IN PROGRESS. Scott's own screenshots show
   * every series collapsing at the September edge, which reads as a
   * business-wide crash; this is the flag that lets the chart mark it rather
   * than draw a fall that did not happen.
   */
  partialFinalBucket: boolean;
  clubs: PortalGroupClub[];
  /**
   * ⚠️ FALSE → the two CRM charts are ABSENT, not empty. A scope with no CRM
   * link has no internet-lead or sales figures at all, and an empty chart reads
   * as a measured nothing.
   */
  hasCrm: boolean;
}

export interface PortalOverviewReport {
  range: { from: string; to: string };
  grouping: PortalGrouping;
  /** The accounts this report covers (post-narrowing). Names only. */
  accounts: Array<{ id: string; name: string }>;
  /** The account a location drill-down is scoped to, if any. */
  selectedAccountId: string | null;
  combined: PortalChannelBlock;
  google: PortalChannelBlock;
  meta: PortalChannelBlock;
  /** ⚠️ Present only when the scope holds more than one account. */
  byLocation: PortalLocationRow[] | null;
  /**
   * §8 — the per-club growth charts.
   *
   * ⚠️ NULL for a single-account scope (there is no group to compare) and for a
   * scope with no measurable clubs. The OVERVIEW only: charts 1 and 2 are CRM,
   * which is a club-level fact that cannot be attributed to a channel, so a
   * channel page carrying them would invite exactly the per-channel reading the
   * CRM rules forbid.
   */
  groupSeries: PortalGroupSeries | null;
  /**
   * RESTYLE — both channels’ campaigns in one table, each tagged with its
   * platform. A union at this layer; neither channel’s own query changed.
   */
  campaigns: PortalCombinedCampaignRow[];
  /** RESTYLE — who to contact. Null when the scope’s accounts disagree. */
  accountManager: PortalAccountManager | null;
  /**
   * The club's own leads and sales, from the client's CRM.
   *
   * ⚠️ NULL when this scope has no CRM source at all, and the panel is then
   * OMITTED rather than emptied — the same posture as every other absent section
   * here. It rides the OVERVIEW payload only: it is a club-level fact about the
   * whole business, not a Meta one or a Google one, and putting it on a channel
   * page would invite exactly the per-channel reading §1 says the data cannot
   * support.
   */
  crm: PortalCrmBlock | null;
  /**
   * ⚠️ THE AUTOMOTIVE HOME (2026-11-11) — the same Meta automotive report the Meta
   * page carries, resolved for the Home page's Meta strip. Null/absent unless the
   * scope's resolved template is `automotive`.
   */
  automotiveReport?: PortalMetaAutomotiveReport | null;
  /**
   * ⚠️ THE AUTOMOTIVE HOME'S GOOGLE STRIP (2026-11-12) — the same Google groups the
   * Google page carries. Null/absent unless the template is `automotive` AND the
   * scope runs Google.
   */
  automotiveGoogleReport?: PortalGoogleAutomotiveReport | null;
  /** RESTYLE — where each figure came from, and when it was pulled. */
  provenance: PortalProvenanceLine[];
  labels: PortalReportLabels;
  /**
   * The brand this report belongs to, or null.
   *
   * ⚠️⚠️ Phase 2 ships this so the CLIENT can resolve the brand THEME. The
   * server has always resolved it (it is how `labels` are chosen) but never
   * sent it, so the browser called `resolvePortalReportConfig(null)` on every
   * report and could not know whose brand it was showing.
   *
   * ⚠️ `null` for a MULTI-BRAND scope is correct, not a gap: a group owner
   * spanning two marques gets kyck's own look rather than one of their brands
   * applied to the other's numbers. Do not "improve" it by picking the first.
   */
  brandAccountId: string | null;
  /**
   * The brand's account NAME, or null.
   *
   * ⚠️ Not decoration: it is the header lockup's fallback when a theme has no
   * `logoUrl` — and a theme with no logo is the NORMAL case, since only two
   * brands have artwork. It also carries the theme binding while brand account
   * ids are unknown (see `BRAND_ACCOUNT_THEMES`).
   */
  brandName: string | null;
  /** ⚠️ Which report to render. Resolved server-side; default off. */
  restyledReport: boolean;
  /**
   * ⚠️⚠️ WHETHER THE REPORT MENTIONS GOOGLE AT ALL. False removes the channel
   * ENTIRELY — the tab, the page, the "Meta against Google" band, the platform
   * column and filter, the provenance line, AND Google's contribution to
   * `combined`.
   *
   * ⚠️ NOT "fetched and hidden". If Google spend still summed into `combined`
   * while the surfaces were hidden, "Where the money went" would carry Google
   * campaign rows with the platform badge suppressed — Google campaigns
   * presented as Meta ones — and the headline total would exceed everything the
   * client can see. An unexplainable total is the exact failure this report's
   * rules exist to prevent.
   *
   * ⚠️ Resolved from `Account.services`, `.some()` across the scope: a group
   * owner with one Google-running club sees Google. See
   * `channelVisibleForScope`.
   *
   * ⚠️ On ALL THREE payloads, because the header nav renders from whichever
   * report loaded and the Meta page is one of them.
   */
  showsGoogle: boolean;
  /**
   * ⚠️ Whether the Website Analytics (GA4) tab exists for this scope. Resolved
   * server-side from the GA4 property registry and the audience rule — the
   * browser never decides. On all four payloads for the same reason as
   * `showsGoogle`: the tab strip renders from whichever report loaded.
   */
  showsWebsite: boolean;
  /** ClubWise data tab (Anytime Fitness). Optional: absent = no tab. */
  showsClubwise?: boolean;
  /**
   * ⚠️ Whether this scope gets the goal-split Meta report — the same
   * brand-relationship answer that fills `PortalMetaReport.goalReport`, shipped
   * on ALL THREE payloads because the Overview and Google pages carry the
   * goal-split wording and the temporary tracking notice too. Resolved
   * server-side; never chosen by the browser.
   */
  goalSplitReport: boolean;
  /**
   * ⚠️ THE RESOLVED CLIENT REPORT TEMPLATE (2026-11-11) — standard / goal_split /
   * automotive, resolved SERVER-SIDE by `resolveReportTemplate` (the client never
   * resolves it) and shipped on every payload. Absent reads as `standard`.
   * `goalSplitReport` is its goal_split case, kept so existing readers need no change.
   */
  reportTemplate?: ReportTemplate;
}

/** GET /api/portal/report/meta */
export interface PortalMetaReport {
  range: { from: string; to: string };
  grouping: PortalGrouping;
  accounts: Array<{ id: string; name: string }>;
  selectedAccountId: string | null;
  block: PortalChannelBlock;
  campaigns: PortalCampaignRow[];
  creatives: PortalCreativeRow[];
  /**
   * RESTYLE — age, gender AND `publisher_platform` (Instagram vs Facebook), all
   * three from the SAME `/demographics` shape.
   *
   * ⚠️⚠️ NULL ON A MULTI-ACCOUNT SCOPE, and the section is then OMITTED rather
   * than emptied. Every account has its OWN stored pull windows, so "the window
   * that answers this range" differs per account and summing two of them would
   * mix grains inside one figure — the read core refuses to do it and there is
   * no rule yet for what to do when accounts disagree. A visible absence is
   * recoverable; a plausible empty section is not.
   *
   * ⚠️ CLIENT-FACING, so the window-grain sentence travels with it, and
   * Instagram vs Facebook carries EXACTLY the same caveat as age and gender —
   * it is a demographics breakdown, not a daily fact.
   */
  demographics: PortalDemographics | null;
  /** The campaigns present in the period — the filter's vocabulary. */
  campaignOptions: PortalCampaignOption[];
  /** Echoed back so the client can tell "filtered to none" from "no filter". */
  appliedCampaignIds: string[] | null;
  byLocation: PortalLocationRow[] | null;
  labels: PortalReportLabels;
  /** See `PortalOverviewReport.brandAccountId` — null means kyck default. */
  brandAccountId: string | null;
  /** See `PortalOverviewReport.brandName`. */
  brandName: string | null;
  /** ⚠️ Which report to render. Resolved server-side; default off. */
  restyledReport: boolean;
  /**
   * ⚠️⚠️ WHETHER THE REPORT MENTIONS GOOGLE AT ALL. False removes the channel
   * ENTIRELY — the tab, the page, the "Meta against Google" band, the platform
   * column and filter, the provenance line, AND Google's contribution to
   * `combined`.
   *
   * ⚠️ NOT "fetched and hidden". If Google spend still summed into `combined`
   * while the surfaces were hidden, "Where the money went" would carry Google
   * campaign rows with the platform badge suppressed — Google campaigns
   * presented as Meta ones — and the headline total would exceed everything the
   * client can see. An unexplainable total is the exact failure this report's
   * rules exist to prevent.
   *
   * ⚠️ Resolved from `Account.services`, `.some()` across the scope: a group
   * owner with one Google-running club sees Google. See
   * `channelVisibleForScope`.
   *
   * ⚠️ On ALL THREE payloads, because the header nav renders from whichever
   * report loaded and the Meta page is one of them.
   */
  showsGoogle: boolean;
  /**
   * ⚠️ Whether the Website Analytics (GA4) tab exists for this scope. Resolved
   * server-side from the GA4 property registry and the audience rule — the
   * browser never decides. On all four payloads for the same reason as
   * `showsGoogle`: the tab strip renders from whichever report loaded.
   */
  showsWebsite: boolean;
  /** ClubWise data tab (Anytime Fitness). Optional: absent = no tab. */
  showsClubwise?: boolean;
  /**
   * ⚠️ Whether this scope gets the goal-split Meta report — the same
   * brand-relationship answer that fills `PortalMetaReport.goalReport`, shipped
   * on ALL THREE payloads because the Overview and Google pages carry the
   * goal-split wording and the temporary tracking notice too. Resolved
   * server-side; never chosen by the browser.
   */
  goalSplitReport: boolean;
  /**
   * ⚠️ THE RESOLVED CLIENT REPORT TEMPLATE (2026-11-11) — standard / goal_split /
   * automotive, resolved SERVER-SIDE by `resolveReportTemplate` (the client never
   * resolves it) and shipped on every payload. Absent reads as `standard`.
   * `goalSplitReport` is its goal_split case, kept so existing readers need no change.
   */
  reportTemplate?: ReportTemplate;
  /**
   * ⚠️⚠️ THE GOAL-SPLIT META REPORT (2026-09-24) — conversions broken out by
   * campaign goal instead of one "Ad Conversions" total. See
   * `src/lib/portalReport/metaGoalReport.ts`.
   *
   * NULL unless EVERY account in scope sits under a brand whose account carries
   * `portalMetaGoalReport`. Resolved server-side from the brand relationship —
   * never from an account id or a brand name — so a location added under the
   * brand inherits the report the day it is added. A mixed scope gets the
   * ordinary Meta page.
   */
  goalReport: PortalMetaGoalReport | null;
  /**
   * The campaign-goal filter applied to the creatives and the audience sections.
   * Null = no filter. Echoed back so the page can tell which filter produced the
   * sections on screen.
   */
  appliedGoalBucket: MetaGoalBucket | null;
  /**
   * ⚠️⚠️ THE AUTOMOTIVE META REPORT (2026-11-11) — null unless the scope's
   * resolved template is `automotive`. See src/lib/portalReport/automotiveReport.ts.
   */
  automotiveReport?: PortalMetaAutomotiveReport | null;
}

// ---------------------------------------------------------------------------
// The goal-split Meta report (2026-09-24)
// ---------------------------------------------------------------------------

/**
 * The four campaign-goal buckets, in panel order.
 *
 * ⚠️ The SQL predicates that assign a campaign to one of these live in
 * `server/modules/reporting/query/campaignClass.ts` and NOWHERE ELSE. This file
 * is only the vocabulary.
 */
export type MetaGoalBucket = 'lead_gen' | 'purchase' | 'other' | 'uncategorised';

export const META_GOAL_BUCKETS: readonly MetaGoalBucket[] = [
  'lead_gen',
  'purchase',
  'other',
  'uncategorised',
];

export function isMetaGoalBucket(v: unknown): v is MetaGoalBucket {
  return typeof v === 'string' && (META_GOAL_BUCKETS as readonly string[]).includes(v);
}

/**
 * Additive counts only. ⚠️ No `reach`: it counts unique people and cannot be
 * summed — see the note at `MetaGoalCountKey` in metaGoalReport.ts.
 */
export interface MetaGoalCounts {
  leads: number;
  purchases: number;
  landingPageViews: number;
  /**
   * ⚠️ IMPRESSIONS ARE AN EVENT COUNT AND SUM SAFELY; REACH DOES NOT (round 2,
   * 2026-09-25). Added so the Meta headline and Other Campaigns can show them.
   * Do not let this be the reason reach comes back — see `MetaGoalCountKey`.
   */
  impressions: number;
}

export type MetaGoalCostPer = 'costPerLead' | 'costPerPurchase' | 'costPerLandingPageView';

/** One currency's money inside a panel. Never blended across currencies. */
export interface MetaGoalMoney {
  currency: string;
  spend: number;
  purchaseValue: number;
  /**
   * ⚠️ ONLY THE PANEL'S OWN GOALS ARE PRESENT. An extra is a number, not an
   * efficiency metric, so it never has a key here — and Uncategorised has no
   * keys at all. Null = the goal count was zero (no cost-per of nothing).
   */
  costPer: Partial<Record<MetaGoalCostPer, number | null>>;
}

export interface PortalMetaGoalPanel {
  bucket: MetaGoalBucket;
  counts: MetaGoalCounts;
  money: MetaGoalMoney[];
  /**
   * The panel's own chart — its PRIMARY metric over the reporting period and
   * over the last twelve months. NULL for Uncategorised (Scott, round 2) and for
   * any panel whose spec names no chart metric.
   *
   * ⚠️⚠️ THE COUNT RIDES IN `leads`, whatever the metric is. The shared chart
   * core reads a count off `PortalSeriesPoint.leads`, and reusing it is what
   * gives these charts the report's gap / partial-month / never-a-zero rules by
   * construction instead of by a second implementation. `metric` says which
   * count it really is. Since 2026-09-25 `spend` is REAL and `costPerLead`
   * carries the panel's own cost-per (named by `costPer`), derived per bucket by
   * the shared core; both are null when the chart may not carry money.
   */
  series: PortalMetaGoalPanelSeries | null;
}

export interface PortalMetaGoalPanelSeries {
  metric: keyof MetaGoalCounts;
  /**
   * The cost-per this chart can plot, or null when it cannot plot money —
   * either the panel has no cost-per, or the panel's rows span more than one
   * currency and a blended cost line would be a fiction. Null ⇒ the chart
   * offers the count chip only (no spend, no cost-per).
   * ⚠️ The VALUE rides in PortalSeriesPoint.costPerLead whatever this says, the
   * same way the count rides in `leads`.
   */
  costPer: MetaGoalCostPer | null;
  /** The one currency the money on this chart is in; null when `costPer` is null. */
  currency: string | null;
  /** The selected period, at the requested grouping. */
  period: PortalSeriesPoint[];
  /** Twelve months ending in the current (partial) month. */
  months: PortalSeriesPoint[];
}

export interface PortalMetaGoalHeadline {
  /** Meta `total_leads` (meta + website) across the whole scope. */
  metaLeads: number;
  /** Meta purchases — the `purchase` action, NEVER the `conversions` column. */
  metaPurchases: number;
  metaLandingPageViews: number;
  /** Meta impressions — an event count, safe to sum (unlike reach). */
  metaImpressions: number;
  /**
   * metaLeads + metaPurchases. ⚠️⚠️ META ONLY SINCE ROUND 2 (2026-09-25): Google
   * was removed from the Meta page entirely, and a headline still carrying one
   * Google conversion read 60 above stats summing to 59. There is no Google
   * field on this type any more, so a Google contribution cannot creep back in.
   */
  total: number;
  /** Total Meta spend, per currency. */
  metaSpend: Array<{ currency: string; spend: number }>;
}

export interface PortalMetaGoalReport {
  headline: PortalMetaGoalHeadline;
  /** Always all four, in `META_GOAL_BUCKETS` order — an empty panel is zeros. */
  panels: PortalMetaGoalPanel[];
  /**
   * Whether the panels summed to the independently-queried headline on leads,
   * purchases, landing page views and spend. False is a defect, and is logged.
   */
  reconciles: boolean;
}

/** GET /api/portal/report/google */
export interface PortalGoogleReport {
  range: { from: string; to: string };
  grouping: PortalGrouping;
  accounts: Array<{ id: string; name: string }>;
  selectedAccountId: string | null;
  block: PortalChannelBlock;
  campaigns: PortalCampaignRow[];
  /**
   * RESTYLE — the "Ad group" side of the "Where the budget goes" toggle.
   *
   * ⚠️ `rows` EMPTY means this client has no ad groups at all (a PMAX-only
   * account), and the toggle is then ABSENT rather than present-and-empty. An
   * empty table behind a control the client just clicked reads as a fault.
   */
  adGroups: PortalAdGroupsResult;
  searchTerms: PortalSearchTermRow[];
  /**
   * RESTYLE — the competitive position block.
   *
   * ⚠️ Every field may be null (see `PortalCompetitivePosition`); when ALL THREE
   * are null the section is OMITTED, because a card of three dashes tells a
   * client nothing and looks like a fault.
   */
  competitive: PortalCompetitivePosition;
  /** True when the term list was capped — the UI states the real total. */
  searchTermsCapped: boolean;
  searchTermsTotal: number;
  campaignOptions: PortalCampaignOption[];
  appliedCampaignIds: string[] | null;
  /**
   * ⚠️⚠️ THE AUTOMOTIVE GOOGLE GROUPS (2026-11-12) — VLA / Search / PMax / Other.
   * Null/absent unless the scope's resolved template is `automotive`. See
   * src/lib/portalReport/automotiveGoogle.ts.
   */
  automotiveGoogleReport?: PortalGoogleAutomotiveReport | null;
  byLocation: PortalLocationRow[] | null;
  labels: PortalReportLabels;
  /** See `PortalOverviewReport.brandAccountId` — null means kyck default. */
  brandAccountId: string | null;
  /** See `PortalOverviewReport.brandName`. */
  brandName: string | null;
  /** ⚠️ Which report to render. Resolved server-side; default off. */
  restyledReport: boolean;
  /**
   * ⚠️⚠️ WHETHER THE REPORT MENTIONS GOOGLE AT ALL. False removes the channel
   * ENTIRELY — the tab, the page, the "Meta against Google" band, the platform
   * column and filter, the provenance line, AND Google's contribution to
   * `combined`.
   *
   * ⚠️ NOT "fetched and hidden". If Google spend still summed into `combined`
   * while the surfaces were hidden, "Where the money went" would carry Google
   * campaign rows with the platform badge suppressed — Google campaigns
   * presented as Meta ones — and the headline total would exceed everything the
   * client can see. An unexplainable total is the exact failure this report's
   * rules exist to prevent.
   *
   * ⚠️ Resolved from `Account.services`, `.some()` across the scope: a group
   * owner with one Google-running club sees Google. See
   * `channelVisibleForScope`.
   *
   * ⚠️ On ALL THREE payloads, because the header nav renders from whichever
   * report loaded and the Meta page is one of them.
   */
  showsGoogle: boolean;
  /**
   * ⚠️ Whether the Website Analytics (GA4) tab exists for this scope. Resolved
   * server-side from the GA4 property registry and the audience rule — the
   * browser never decides. On all four payloads for the same reason as
   * `showsGoogle`: the tab strip renders from whichever report loaded.
   */
  showsWebsite: boolean;
  /** ClubWise data tab (Anytime Fitness). Optional: absent = no tab. */
  showsClubwise?: boolean;
  /**
   * ⚠️ Whether this scope gets the goal-split Meta report — the same
   * brand-relationship answer that fills `PortalMetaReport.goalReport`, shipped
   * on ALL THREE payloads because the Overview and Google pages carry the
   * goal-split wording and the temporary tracking notice too. Resolved
   * server-side; never chosen by the browser.
   */
  goalSplitReport: boolean;
  /**
   * ⚠️ THE RESOLVED CLIENT REPORT TEMPLATE (2026-11-11) — standard / goal_split /
   * automotive, resolved SERVER-SIDE by `resolveReportTemplate` (the client never
   * resolves it) and shipped on every payload. Absent reads as `standard`.
   * `goalSplitReport` is its goal_split case, kept so existing readers need no change.
   */
  reportTemplate?: ReportTemplate;
}

// ---------------------------------------------------------------------------
// Per-brand composability
// ---------------------------------------------------------------------------

/**
 * ⚠️ THE LABEL INDIRECTION EXISTS NOW SO THE CRM PHASE DOES NOT HAVE TO TOUCH
 * EVERY COMPONENT. Scott: *"the report for each Brand will be slightly different
 * and connected to different BigQuery sources for CRM data."* A gym counts
 * "members", a dealership counts "sales" — v1 only needs leads / CPL / spend,
 * but every component already reads its wording from here rather than hardcoding
 * "Leads", so adding a brand is a registry entry rather than a sweep.
 */
export interface PortalReportLabels {
  leads: string;
  leadsSingular: string;
  costPerLead: string;
  spend: string;
  /** e.g. "Cost per member" needs a matching chart-axis short form. */
  leadsShort: string;
  /**
   * The CRM vocabulary (2026-08-31). Snap says "sales"; the indirection is
   * already here for a brand that says "members" or "signups", which is exactly
   * the case this label set was built for.
   */
  sales: string;
  salesSingular: string;
  costPerSale: string;
  conversionRate: string;
  /**
   * The CRM's TOTAL enquiry count (§3, 2026-09-03).
   *
   * ⚠️⚠️ ITS OWN ENTRY, KEYED BY THE NUMBER RATHER THAN COMPOSED FROM
   * `leads`. The hero built `Total ${labels.leads.toLowerCase()}`, which with
   * `leads: 'Ad Conversions'` printed "Total ad conversions" over
   * `crm.totals.leads` — `SUM(leads)` from GymSales, every enquiry the club
   * recorded from every source. So 110 was the whole enquiry count wearing the
   * ad platforms' name, and 110 minus 74 read as "non-internet ad conversions",
   * a quantity that does not exist.
   *
   * ⚠️ COMPOSITION IS THE BUG, NOT THE WORDING: any label built from another
   * metric's label inherits that metric's meaning. A brand renaming `leads`
   * would silently rename this too.
   */
  crmTotalLeads: string;
  /**
   * ⚠️ OPTIONAL BRAND VOCABULARY for the digital-lead figures (2026-10-17). Absent
   * = "Internet leads" / "Cost per internet lead" (GymSales: Snap, Jetts — those
   * reports must not change). Anytime Fitness sets "Digital leads" / "Cost per
   * digital lead", because its digital definition is wider than an online form.
   * Read ONLY through `digitalLeadsLabel` / `costPerDigitalLeadLabel`.
   */
  digitalLeads?: string;
  costPerDigitalLead?: string;
}

/**
 * The labels the CRM band may read — the CRM vocabulary and nothing else.
 *
 * ⚠️⚠️ A NARROWING THAT MAKES THE §3 DEFECT A COMPILE ERROR. `labels.leads`
 * is not in scope inside that component, so `Total ${labels.leads}` does not
 * typecheck: the wrong composition is unavailable rather than merely
 * discouraged. It is a `Pick`, so there is nothing to keep in step.
 *
 * ⚠️ AND IT IS THE RIGHT BOUNDARY ANYWAY: a band counting what the CLUB
 * recorded has no business naming an AD-PLATFORM metric. `spend` is absent
 * because the band's own "Total media spend" is a literal.
 */
export type PortalCrmLabels = Pick<
  PortalReportLabels,
  'crmTotalLeads' | 'sales' | 'costPerSale' | 'conversionRate' | 'digitalLeads' | 'costPerDigitalLead'
>;

/**
 * The sections a brand's report renders, in order. ⚠️ `crm` is a DELIBERATE
 * placeholder: it is in the vocabulary and absent from every v1 config, so the
 * later per-brand CRM phase adds a renderer and a config entry rather than
 * reshaping the page.
 */
export type PortalReportSectionId =
  | 'combined'
  | 'google'
  | 'meta'
  | 'byLocation'
  | 'campaigns'
  | 'creatives'
  | 'searchTerms'
  | 'crm';

// ---------------------------------------------------------------------------
// Per-brand theme (Phase 2)
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ BRAND ACCENTS ON KYCK CHROME — NOT A SKIN.
 *
 * A brand supplies this small set of values; the surfaces, ink, spacing and
 * structure stay kyck's. Scott's own Snap proposal is the reference design and
 * it already works this way: two coexisting token sets where the KYCK tokens
 * dominate the usage. Two reasons it is drawn this narrowly —
 *
 *   · the report stays recognisably a kyck product, which matters the moment a
 *     group owner opens reports for two different marques;
 *   · a new brand is a config entry rather than a redesign.
 *
 * ⚠️⚠️ NOTHING HERE MAY REACH A DATA COLOUR. Chart series, coverage-state fills
 * and delta up/down are FIXED across every brand — see the note on
 * `PortalBrandTheme.accent` and the tests that pin it.
 */
export interface PortalBrandTheme {
  /**
   * The colour that says "this is the brand".
   *
   * ⚠️⚠️ IT IS A CHROME COLOUR AND NEVER A DATA COLOUR. Snap's brand colour is
   * RED: wire this into a chart series and every Snap performance chart turns
   * red, which reads as loss on a page of results. (Fitstop's orange is barely
   * better.) A brand palette and a data palette answer different questions.
   * Google must also be the same colour on every report, or a client comparing
   * two months watches the palette move under them.
   */
  accent: string;
  /** Optional darker cut, for gradients and hovers. */
  accentDark?: string;
  /** The brand's near-black, for headings. */
  ink: string;
  /** Optional full CSS gradient value. */
  gradient?: string;
  /**
   * A file under `/brand/`.
   *
   * ⚠️ OPTIONAL, and its absence must degrade to the brand NAME as text — an
   * unthemed or logo-less brand is the normal case, not an edge case.
   */
  logoUrl?: string;
  logoAlt?: string;
  /** font-family stack. Absent → kyck default (Geist). */
  fontDisplay?: string;
  fontBody?: string;
  fontMono?: string;
}

export interface PortalReportConfig {
  labels: PortalReportLabels;
  /**
   * The resolved brand theme, or null for kyck's own look.
   *
   * ⚠️ null is the DEFAULT and the fallback for an unknown brand, a multi-brand
   * scope, and a brand with no theme registered. A missing theme must never be
   * the reason a client cannot read their report.
   */
  theme: PortalBrandTheme | null;
  /** Overview page section order. */
  overviewSections: PortalReportSectionId[];
  metaSections: PortalReportSectionId[];
  googleSections: PortalReportSectionId[];
}

// ---------------------------------------------------------------------------
// RESTYLE (2026-08-30) — the additional wire shapes the new presentation needs.
//
// ⚠️ EVERY ONE OF THESE IS READ-PATH WORK OVER DATA ALREADY IN BIGQUERY. This
// shipment touches no pull script and no DDL — the correction at the top of
// `_architect/briefs/2026-08-30-client-report-restyle.md` records why that was
// worth checking rather than asserting.
//
// ⚠️ THERE IS NO CREATIVE FORMAT FIELD (9:16 / 4:5 / 1:1). It was CUT, not
// deferred, and it must not be reintroduced by inferring an aspect ratio from a
// thumbnail's dimensions: a square crop of a vertical video makes that guess
// wrong, and nobody asked for the field.
// ---------------------------------------------------------------------------

/**
 * Twelve rolling months ending in the current month, against the SAME twelve
 * calendar months a year earlier.
 *
 * ⚠️⚠️ `previous` IS EITHER COMPLETE OR ABSENT. An account with under two years
 * of history gets `previous: []` and a `previousUnavailableReason` the page
 * states in words — never a half-drawn 2025 series whose empty months read as a
 * collapse rather than as the absence of measurement. That is the report's
 * no-data-is-not-zero rule applied to a whole series instead of a bucket.
 *
 * ⚠️ `previous[i]` is the same CALENDAR month as `current[i]`, one year back —
 * not the same number of days into the month. The page says so, because a
 * partial current month compared against a complete one a year ago is a real
 * distortion the reader has to be told about.
 */
export interface PortalYearOnYear {
  current: PortalSeriesPoint[];
  previous: PortalSeriesPoint[];
  previousUnavailableReason: string | null;
  /** e.g. '2026' / '2025' — the labels the legend shows. */
  currentLabel: string;
  previousLabel: string;
}

/**
 * Google's competitive position — how often the ads could have shown and did not.
 *
 * ⚠️⚠️ A SHARE METRIC'S ZERO MEANS "NOT APPLICABLE", NOT 0%. Google writes 0.0
 * rather than NULL on rows where a search share cannot apply (a PMAX campaign's
 * YouTube rows), so NULL-ness alone cannot tell the two apart — see
 * `reportSpecs.ts` rule 4 and `competitivenessSql`. These arrive already
 * resolved through the same eligible-impression denominator the staff dashboard
 * uses, and a metric that cannot be measured is NULL here and renders as
 * unavailable, never as a measured zero.
 *
 * ⚠️ Percentages, 0-100, one decimal — the same scale `computeMetrics` returns.
 */
export interface PortalCompetitivePosition {
  /** Of the searches you could have appeared for, the share you appeared for. */
  impressionShare: number | null;
  /** Missed because the daily budget was already spent — the recoverable share. */
  budgetLostShare: number | null;
  /** Missed on ad rank rather than money. */
  rankLostShare: number | null;
}

// ---------------------------------------------------------------------------
// CRM (2026-08-31) — the club's own leads and sales, from the client's CRM.
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ NOTHING HERE MAY BE BROKEN DOWN BY CHANNEL, AND THAT IS THE DESIGN
 * CONSTRAINT RATHER THAN A LIMITATION OF v1.
 *
 * Measured on Snap's GymSales table (AU, last 12 months): every row carrying
 * `total_sale` has a BLANK `status`, a BLANK `contact_type` and a NULL
 * `person_id` — 188,726 of 188,726 across all history. A sale row's only
 * dimensions are its date and its club. There is no such thing as a Meta sale or
 * a Google sale in this data, so there is no shape this type could take that
 * would carry one, and none may be added by inference.
 *
 * Two consequences the copy has to state rather than imply:
 *   · COST PER SALE divides ALL media spend by ALL sales — walk-ins, referrals
 *     and organic included. Defensible only while the page says so.
 *   · CONVERSION RATE IS CLUB-LEVEL, not an advertising conversion rate. Sales
 *     over every enquiry the club recorded is the only honest reading available,
 *     and both lead figures sit beside it so a reader can see the gap for
 *     themselves.
 */
export interface PortalCrmTotals {
  /**
   * Every enquiry the club recorded, from every source. `SUM(leads)`.
   *
   * ⚠️ NULL ONLY FOR A MONTHLY-GRAIN SOURCE (ClubWise) with no whole uploaded
   * month in the selected range — leads are not measured there, which is not
   * zero. GymSales sources always have a number.
   */
  leads: number | null;
  /** The subset that arrived through an online form. `SUM(internet_leads)`. NULL under the same rule as `leads`. */
  digitalLeads: number | null;
  /**
   * `SUM(total_sale)`.
   *
   * ⚠️⚠️ NEVER `COUNTIF(status = 'Sale')`. Measured, AU, last 12 months: 185,404
   * against 96,860 — the wrong one understates by 48%, and it is the more
   * natural-looking of the two.
   */
  sales: number;
  /**
   * Sales ÷ ALL CRM leads, as a percentage.
   *
   * ⚠️ NULL when there were no leads — a conversion rate of nothing is undefined,
   * not 0%, and a plotted zero would read as "nobody who enquired joined".
   */
  conversionRate: number | null;
}

/**
 * Media spend against sales, in one native currency.
 *
 * ⚠️ Per-currency and never blended, like every other money figure on this
 * report. ⚠️ `costPerSale` is NULL when there were no sales — spend with no
 * sales has no cost per sale, and a $0.00 would read as "the sales were free",
 * which is the flattering direction to be wrong in. Same rule as `costPerLead`.
 */
export interface PortalCrmCostPerSale {
  currency: string;
  spend: number;
  costPerSale: number | null;
}

/**
 * One bucket of the CRM's own series.
 *
 * ⚠️⚠️ COUNTS ONLY. The three ratios a CRM chart can draw — close rate, cost per
 * internet lead, cost per sale — are DERIVED per bucket at the point of use,
 * exactly as `costPerLead` and the delivery ratios are. Three nullable ratio
 * fields on the wire would be three chances for a bucket's ratio to disagree
 * with its own numerator, and two of the three are not even computable here:
 * they need AD SPEND, which is a different source.
 *
 * ⚠️ NULL means UNMEASURED, never zero — the same rule as `PortalSeriesPoint`.
 * A month before this club's CRM history begins is not a month it took no
 * enquiries.
 */
export interface PortalCrmSeriesPoint {
  bucket: string;
  label: string;
  coverage: PortalBucketCoverage;
  leads: number | null;
  digitalLeads: number | null;
  sales: number | null;
  /**
   * ⚠️ CLUBWISE ONLY (absent on every GymSales point, so a Snap / Jetts payload is
   * unchanged). Leads whose Contact Type is "Staff generated" and whose Source is
   * not digital. NULL = month not uploaded, never zero.
   */
  staffLeads?: number | null;
  /**
   * ⚠️ CLUBWISE ONLY. Set on a month whose upload runs only to this day (YYYY-MM-DD),
   * so its tooltip can say "to 20 Oct" rather than just "partial".
   */
  partialTo?: string;
}

/**
 * One row of the lead-source breakdown.
 *
 * ⚠️⚠️ THE VALUE IS THE CRM'S OWN, UNTOUCHED. `source` is free text and dirty —
 * `Social Media` (20,297) sits alongside `Social Meda` (456, one club), `Over
 * 50s` alongside `Over 50's`, and Referral has four spellings — and most values
 * are OFFER names our own landing pages set rather than channels. Normalising is
 * a decision nobody has made, and it would silently merge two clubs' data.
 *
 * ⚠️ It is NOT a channel dimension and must not be mapped to Meta or Google.
 */
export interface PortalCrmSourceRow {
  /** As stored. A blank source is labelled at the render edge, never merged. */
  source: string;
  leads: number;
}

/**
 * The lead-source breakdown for the period.
 *
 * ⚠️⚠️ THE LONG TAIL IS FOLDED AND NOTHING ELSE IS. In particular a blank or
 * `Unknown` source is its OWN row however large — measured on Scott's example it
 * is 82 of 262, the second-largest source, and that is a finding (a third of
 * enquiries are not being tagged by club staff) rather than noise to tidy away.
 */
export interface PortalCrmSources {
  /** Top N by leads, descending. */
  rows: PortalCrmSourceRow[];
  /** The folded tail: how many leads, across how many distinct sources. */
  otherLeads: number;
  otherSources: number;
  /** Every lead with a source row, so a share has an honest denominator. */
  total: number;
}

/**
 * Media spend against internet leads, in one native currency.
 *
 * ⚠️ SEPARATE FROM `PortalCrmCostPerSale` rather than a shared shape, because
 * the two divide by different denominators and reading one for the other is the
 * mistake worth making impossible. ⚠️ NULL with no internet leads.
 */
export interface PortalCrmCostPerDigitalLead {
  currency: string;
  spend: number;
  costPerDigitalLead: number | null;
}

/** Media spend in one native currency. ⚠️ Never blended. */
export interface PortalCrmSpend {
  currency: string;
  spend: number;
}

/** A club whose CRM figures are, or are not, in these totals. */
export interface PortalCrmClub {
  accountId: string;
  name: string;
}

/**
 * The CRM block on the client report.
 *
 * ⚠️⚠️ THE WHOLE BLOCK IS NULL — and the panel OMITTED — when no account in
 * scope has a CRM link, or the brand has no CRM table. That is the
 * omit-a-section-with-no-source half of the report's absence rule: there is
 * nothing to explain, and a placeholder would promise a feature.
 */
export interface PortalCrmBlock {
  /** ⚠️ The CRM's name for the footer; ABSENT for GymSales (Snap, Jetts), whose payload is unchanged. */
  sourceLabel?: string;
  totals: PortalCrmTotals;
  costPerSale: PortalCrmCostPerSale[];
  /**
   * How far back the CRM goes for this scope, and therefore which months of the
   * selected period were measured at all.
   *
   * ⚠️ It is the SAME `PortalCoverage` the ad channels use, on purpose: an
   * unmeasured period must not render as a club that took no enquiries. NZ's
   * GymSales history starts 2024-01-01 and Australia's in 2021, so this genuinely
   * differs by club — and it differs by CLUB, which is why it is measured rather
   * than known.
   */
  coverage: PortalCoverage;
  /** When the CRM feed last landed. Its OWN date — see the provenance rule. */
  asOf: string | null;
  /**
   * ⚠️⚠️ THE CLUBS THESE FIGURES COVER, NAMED. On a mixed scope a club with no
   * CRM link would otherwise contribute a silent zero to every figure, which is
   * rule 15 — an unmeasured thing rendered as a measured nothing — with a
   * conversion rate on top of it. So the counted and uncounted clubs both travel
   * with the block and the panel says which is which.
   */
  countedClubs: PortalCrmClub[];
  uncountedClubs: PortalCrmClub[];
  /**
   * The selected period, bucketed the SAME way the ad series is.
   *
   * ⚠️⚠️ THE SAME GROUPING IS LOAD-BEARING, not tidiness. The cost ratios divide
   * AD SPEND by a CRM COUNT — the first cross-source ratio on this report — and
   * they are joined BY BUCKET KEY. Two different bucketings would either fail to
   * join at all or, worse, join a week to a month.
   */
  periodSeries: PortalCrmSeriesPoint[];
  /**
   * The twelve rolling months, over exactly `yearOnYearRange`'s window.
   *
   * ⚠️ The same window the ad blocks' `yearOnYear.current` covers, so the keys
   * line up for the same reason.
   */
  monthlySeries: PortalCrmSeriesPoint[];
  /**
   * The same totals over THE COMPARISON PERIOD THE CLIENT SELECTED.
   *
   * ⚠️⚠️ THE SELECTOR DRIVES THE BAND (2026-09-03). This was pinned to
   * year-on-year unconditionally, so a client choosing "vs previous month"
   * watched the ad blocks move while the band kept comparing against last year
   * and kept SAYING so. Year-on-year remains the DEFAULT; it is no longer the
   * only basis.
   *
   * ⚠️⚠️ WHAT HAS NOT CHANGED IS THE NO-MIXING RULE: every delta on the band
   * shares one basis, stated once. A metric that cannot do the SELECTED
   * comparison shows no delta rather than quietly falling back to another one,
   * because a reader compares the percentages rather than the footnotes.
   *
   * ⚠️ NULL when the CRM does not cover that window: complete or absent, which
   * is `PortalYearOnYear`'s own contract rather than a second half-measured
   * comparison.
   */
  previous: PortalCrmTotals | null;
  /** Why there is no comparison, when there is none. Shown instead of a figure. */
  previousUnavailableReason: string | null;
  /**
   * The comparison window these figures describe, and which basis produced it.
   *
   * ⚠️⚠️ READ FROM THE PAYLOAD, NEVER RECOMPUTED IN THE BROWSER. The band
   * names its own window ("Compared with the same period last month, 1—31 Jul
   * 2026"), and a client-side re-derivation would be a second definition of
   * every comparison mode — which is the drift `portalComparisonRange` exists
   * to prevent, arriving one layer later.
   *
   * ⚠️ BOTH FIELDS ARE SET FROM ONE `CrmComparison`, so the stated window and
   * the stated basis cannot disagree with each other or with the figures.
   *
   * ⚠️ Present even when `previous` is null: the band still has to say WHICH
   * window it could not compare against.
   */
  previousRange: { from: string; to: string };
  previousComparisonMode: PortalComparisonMode;
  /**
   * ⚠️ The media spend the two cost figures divide, per currency — shown on the
   * band because it is their NUMERATOR, which makes them auditable rather than
   * assertions.
   */
  spendByCurrency: PortalCrmSpend[];
  /**
   * ⚠️⚠️ LAST YEAR'S HALVES, or NULL. A cost delta needs last year's SPEND as
   * well as last year's CRM count, and on a real club the spend is the half that
   * is missing — ad history starts long after the CRM does. So the counts carry
   * year-on-year deltas and the costs carry none, which is correct and has to
   * look deliberate rather than like three broken tiles.
   */
  previousSpendByCurrency: PortalCrmSpend[] | null;
  previousCostPerSale: PortalCrmCostPerSale[] | null;
  previousCostPerDigitalLead: PortalCrmCostPerDigitalLead[] | null;
  /** ⚠️ Per currency, like every money figure a client sees. */
  costPerDigitalLead: PortalCrmCostPerDigitalLead[];
  sources: PortalCrmSources;
  /**
   * ⚠️⚠️ NON-NULL ONLY FOR A MONTHLY-GRAIN LEAD SOURCE (ClubWise). Says which
   * whole, uploaded months the LEAD figures (and the conversion rate's sales)
   * cover, which months were left out because they have not been uploaded, and
   * the sentence the panel prints above the figures. A part-month range includes
   * no month it does not fully cover, and a month nobody uploaded is "not
   * uploaded" — never zero.
   */
  leadBasis?: PortalCrmLeadBasis | null;
  /**
   * ⚠️⚠️ CLUBWISE ONLY. Omitted entirely (not null) for every other source, so a
   * GymSales report payload is byte-identical to before it existed.
   */
  clubwise?: PortalClubwiseBlock;
}

/** The channels a ClubWise lead is reported under, in display order. */
export type PortalClubwiseChannel =
  | 'meta'
  | 'website'
  | 'staff'
  | 'other';

export interface PortalClubwiseChannelRow {
  channel: PortalClubwiseChannel;
  label: string;
  leads: number;
  /** Leads from the measured months who have joined so far (cohort join). */
  joined: number;
  /** joined / leads, percent to one decimal. NULL with no leads. */
  conversionRate: number | null;
}

/**
 * The ClubWise additions to the CRM block. AGGREGATES ONLY: no member-level row
 * of any kind reaches the client.
 */
export interface PortalClubwiseBlock {
  /** The uploaded months every figure here covers. */
  measuredMonths: string[];
  /** ⚠️ Months among them that are part-way through, with the day each upload runs to. */
  monthToDate?: Array<{ month: string; dataTo: string }>;
  /** Staff generated leads over those months. NULL = no whole uploaded month. */
  staffLeads: number | null;
  /**
   * The previous equivalent whole months, and the lead figures over them. NULL
   * unless EVERY one of those months is uploaded: a delta against a part-measured
   * window would be a delta against an understatement.
   */
  previousMonths: string[] | null;
  previous: {
    leads: number;
    digitalLeads: number;
    staffLeads: number;
    conversionRate: number | null;
  } | null;
  /** All four channels, always, in display order (zero rows included). */
  channels: PortalClubwiseChannelRow[];
  total: { leads: number; joined: number; conversionRate: number | null };
  /**
   * Leads in the window from an upload made before channels were stored. They
   * count in `total` but belong to no channel row; non-zero means "re-upload".
   */
  unclassifiedLeads: number;
  /** The ClubWise data tab's tables. Aggregates only. */
  detail?: PortalClubwiseDetail;
  /**
   * The Sources and Contact Types behind each channel row (the channel table's
   * click-to-expand). Aggregates only; each split sums to its channel's leads.
   * Omitted when there is no whole uploaded month.
   */
  channelSplits?: Partial<Record<PortalClubwiseChannel, PortalClubwiseChannelSplit>>;
}

export interface PortalClubwiseChannelSplit {
  sources: PortalClubwiseBreakdownRow[];
  contactTypes: PortalClubwiseBreakdownRow[];
}

/** One row of a leads breakdown (contact type, source). */
export interface PortalClubwiseBreakdownRow {
  label: string;
  leads: number;
  /** Leads from the measured months who have joined so far (cohort join). */
  joined: number;
  conversionRate: number | null;
}

/** Joins (sales) under a label, and how many came from an earlier month's lead. */
export interface PortalClubwiseSalesRow {
  label: string;
  sales: number;
  fromEarlierLeads: number;
}

export interface PortalClubwiseCountRow {
  label: string;
  count: number;
}

export interface PortalClubwiseStatusRow {
  label: string;
  count: number;
  kind: 'joined' | 'open' | 'lost' | 'unknown';
}

export interface PortalClubwiseTrendMonth {
  month: string;
  /** False = not uploaded for every club: not measured, never zero. */
  uploaded: boolean;
  channels: Array<{ channel: PortalClubwiseChannel; leads: number; joined: number }> | null;
}

/**
 * ⚠️⚠️ AGGREGATES ONLY — no member-level row of any kind. Every figure covers the
 * whole uploaded months in `measuredMonths`, and `joined` is a cohort figure that
 * rises as later months are uploaded.
 */
export interface PortalClubwiseDetail {
  contactTypes: PortalClubwiseBreakdownRow[];
  sources: PortalClubwiseBreakdownRow[];
  /** ALL joins in the months, including leads entered in earlier months. */
  salesBySource: PortalClubwiseSalesRow[];
  salesByContactType: PortalClubwiseSalesRow[];
  sales: number;
  salesFromEarlierLeads: number;
  membershipTypes: PortalClubwiseCountRow[];
  statuses: PortalClubwiseStatusRow[];
  /** Hot Prospect + Prospect. */
  stillOpen: number;
  quality: {
    leads: number;
    noSource: number;
    noContactType: number;
    unknownStatusLeads: number;
    unknownStatusValues: number;
  };
  /** Twelve months ending at the latest month in view, oldest first. */
  trends: PortalClubwiseTrendMonth[];
}

export interface PortalCrmLeadBasis {
  grain: 'monthly';
  /** YYYY-MM, in order. The months the lead figures cover. */
  measuredMonths: string[];
  /** YYYY-MM, in order. Whole months in the range that at least one club has not uploaded. */
  notUploadedMonths: string[];
  /**
   * ⚠️ Months in `measuredMonths` that are part-way through (the upload runs to
   * `dataTo`, not the month's end). Present only for a monthly-grain source; empty
   * = every measured month is whole. No lead comparison is shown while any exist.
   */
  monthToDate?: Array<{ month: string; dataTo: string }>;
  /** The plain-words caveat, printed ABOVE the figures it qualifies. */
  note: string;
  /** "Leads for October are to 20 Oct (latest upload)." — null when nothing is month-to-date. */
  monthToDateNote?: string | null;
}

/**
 * A campaign row on the combined Overview table.
 *
 * ⚠️ The UNION of the two channels' campaign rows, tagged. Both channels'
 * campaign queries already exist; this is a merge at the portal layer, never a
 * new pull. The badge and the All / Meta / Google filter read `platform`.
 */
export interface PortalCombinedCampaignRow extends PortalCampaignRow {
  platform: 'meta' | 'google';
}

/**
 * Who to contact about this report.
 *
 * ⚠️ RESOLVED, NEVER GUESSED. A scope whose accounts have DIFFERENT account
 * managers resolves to null and the header simply omits the line — naming one
 * of several would send a client to the wrong person, which is worse than
 * naming nobody. `phoneNumber` is already client-facing (it renders in AM email
 * signatures), so surfacing it here exposes nothing new.
 */
export interface PortalAccountManager {
  name: string;
  email: string;
  phone: string | null;
}

/**
 * One line of the provenance block — which system a figure came from, and when
 * it was pulled.
 *
 * ⚠️ SHOWN TO THE CLIENT. It is a brand rule for monthly reports, and it is
 * stronger on a live page than in a PDF: silence about freshness implies live,
 * and Google's transfer lands a day behind Meta's two-hourly pull.
 */
/**
 * The Meta audience breakdowns, exactly as the staff panel reads them.
 *
 * ⚠️⚠️ THE STATED PERIOD IS THE STORED WINDOW, NEVER THE SELECTED RANGE.
 * `getAdAnalytics` takes no time increment, so a breakdown cell is meaningful
 * only relative to the window it was computed over — every other panel on the
 * page may say "1–15 Aug" while this one says 1 Jul – 27 Aug. Widening the
 * label to match the rest of the report is the ONE change that turns a correct
 * panel into a misleading one, so `period` travels beside `range` and the page
 * names both.
 *
 * ⚠️ Reuses the staff wire types rather than restating them: a client and their
 * account manager must not be able to see two different audience splits.
 */
export interface PortalDemographics {
  /** The range that was REQUESTED. */
  range: { from: string; to: string };
  /** Which stored grain answered it, and the period the FIGURES describe. */
  period: ReportingDemographicsPeriod;
  dimensions: ReportingDemographicsDimension[];
  /** When the snapshot behind these figures was pulled. */
  asOf: string | null;
}

/**
 * Which report this client gets.
 *
 * ⚠️⚠️ RESOLVED SERVER-SIDE FROM THE SCOPE, never chosen by the browser. It is
 * a per-account admin toggle, DEFAULT OFF, and it exists so a rollback is a
 * toggle rather than a revert and a deploy.
 *
 * ⚠️ On EVERY report payload, because a client can land on any of the three
 * pages first and each fetches its own — a flag on only the Overview would
 * give a deep link the wrong presentation.
 */
export interface PortalReportPresentation {
  restyledReport: boolean;
}

export interface PortalProvenanceLine {
  /**
   * The PLATFORM the figures come from, as the client knows it — "Meta Ads",
   * "Google Ads", "GymSales".
   *
   * ⚠️⚠️ NEVER OUR PLUMBING. Zernio is a supplier relationship and BigQuery is
   * our warehouse; naming either on a client-facing page discloses a vendor for
   * no benefit to the reader. A separate `source` field used to carry "Meta Ads
   * via Zernio" and was removed rather than emptied — a field nothing renders is
   * the obvious place for a supplier name to reappear.
   */
  what: string;
  /** YYYY-MM-DD of the latest data, or null when we could not read it. */
  asOf: string | null;
}
